import { useEffect, useRef } from 'react';

import { getOnboardingPrice } from '@/features/onboarding/pricing';
import { trackAmplitude } from '@/lib/amplitude';
import { ensureGoogleAnalyticsInit } from '@/lib/google-analytics';
import { getPostHog } from '@/lib/posthog';

export type OnboardingAnalyticsEventMap = {
    onboarding_started: undefined;
    goal_toggled: { goal: string; selected: boolean };
    build_mode_set: { mode: string };
    sources_set: { n: number; est_price: number };
    step_completed: { step: number };
    ig_connect_clicked: undefined;
    ig_connect_succeeded: { accounts: number };
    ig_connect_failed: { reason: string };
    connect_skipped: undefined;
    checkout_redirected: { sources: number; price: number };
    checkout_completed: { sources: number; price: number };
    checkout_canceled: undefined;
};

export type OnboardingAnalyticsEventName = keyof OnboardingAnalyticsEventMap;

const CHECKOUT_COMPLETION_TRACKED_KEY_PREFIX = 'onboarding:checkout-completion-tracked';

export async function trackOnboardingEvent<TEventName extends OnboardingAnalyticsEventName>(
    eventName: TEventName,
    payload?: OnboardingAnalyticsEventMap[TEventName]
) {
    trackAmplitude(eventName, payload as Record<string, unknown> | undefined);
    getPostHog().capture(eventName, payload);

    await ensureGoogleAnalyticsInit();
    window.gtag?.('event', eventName, payload ?? {});
}

function getCheckoutCompletionTrackingKey(organizationId: string | null | undefined, completedAt: string | null) {
    return `${CHECKOUT_COMPLETION_TRACKED_KEY_PREFIX}:${organizationId ?? 'unknown'}:${completedAt ?? 'completed'}`;
}

function getBrowserLocalStorage(): Storage | null {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
}

export async function trackCheckoutCompletedOnce({
    organizationId,
    completedAt,
    sources
}: {
    organizationId: string | null | undefined;
    completedAt: string | null;
    sources: number;
}) {
    if (typeof window === 'undefined') {
        return;
    }

    const trackingKey = getCheckoutCompletionTrackingKey(organizationId, completedAt);
    const storage = getBrowserLocalStorage();

    try {
        if (storage?.getItem(trackingKey) === '1') {
            return;
        }

        storage?.setItem(trackingKey, '1');
    } catch {
        // Analytics should still run if browser storage is unavailable.
    }

    const sourceCount = Math.max(1, Math.round(sources));
    await Promise.allSettled([
        trackOnboardingEvent('checkout_completed', {
            sources: sourceCount,
            price: getOnboardingPrice(sourceCount)
        }),
        trackOnboardingEvent('step_completed', { step: 2 })
    ]);
}

export function useTrackOnboardingStartedOnce() {
    const hasTrackedRef = useRef(false);

    useEffect(() => {
        if (hasTrackedRef.current) {
            return;
        }

        hasTrackedRef.current = true;
        void trackOnboardingEvent('onboarding_started');
    }, []);
}
